// The contract of ONE plan version, read into the model the screens use.
//
// Every route that touches a version answers the same body — GET, the draft
// PATCH, submit, request-changes and approve all go through `#renderVersion` in
// mastermind_core's PlanManager:
//
//   {
//     plan:            { id, project_id, name, document_id, created_by },
//     version:         { id, number, state, revision, author_id,
//                        document_version_id, created_at, updated_at },
//     content:         string | null,       ← NOT inside `version`
//     content_version: number | null,
//     scope:           { repository_ids: number[], whole_project: boolean },
//     approval:        { approved_by, approved_at } | null,
//   }
//
// THE BUG THIS FILE EXISTS FOR. The screens used to take `data.version` and
// read `content` and `repository_ids` off it. Neither lives there, so every
// plan opened as an empty document over the whole project — and a save from
// that screen would have sent the emptiness back. The content, the scope and
// the approval are SIBLINGS of `version`, and one adapter reads them for every
// caller, so load, reload and save cannot drift apart again.
//
// WHAT IS NOT GUESSED:
//
//   - A body without a readable `version` (number, state, revision) THROWS. A
//     screen that cannot say which revision it read cannot save safely, and an
//     empty editor would look like an empty plan.
//   - `content` that is neither a string nor null THROWS, for the same reason.
//     `null` is a legal value and means "this version has no text".
//   - A scope that is missing or malformed is NOT the whole project. It comes
//     back as `SCOPE_UNKNOWN`: the screens say so, never send it back, and do
//     not let anybody sign it.

import { SCOPE_ALL, SCOPE_SOME } from './form.js';

// The same two values the scope chooser uses, plus the one it never offers.
export const SCOPE_WHOLE = SCOPE_ALL;
export const SCOPE_LISTED = SCOPE_SOME;
export const SCOPE_UNKNOWN = 'unknown';

export class PlanContractError extends Error {
    constructor(detail) {
        super(`La respuesta del plan no tiene la forma esperada: ${detail}`);
        this.name = 'PlanContractError';
    }
}

const isObject = value => value != null && typeof value === 'object' && !Array.isArray(value);
const isPositiveInteger = value => Number.isInteger(value) && value > 0;

// `repository_ids` must be an explicit array of ids. An EMPTY array is the
// whole project on purpose; anything that is not an array is not a scope at all.
// `whole_project`, when present, has to agree with the list — if the two
// disagree, neither is trusted.
export function readScope(scope) {
    const unknown = { status: SCOPE_UNKNOWN, repository_ids: null };

    if (!isObject(scope) || !Array.isArray(scope.repository_ids)) return unknown;
    if (!scope.repository_ids.every(isPositiveInteger)) return unknown;

    const ids = [...new Set(scope.repository_ids)].sort((a, b) => a - b);
    const whole = ids.length === 0;

    if (scope.whole_project !== undefined && scope.whole_project !== whole) return unknown;

    return { status: whole ? SCOPE_WHOLE : SCOPE_LISTED, repository_ids: ids };
}

function readApproval(approval) {
    if (!isObject(approval)) return null;
    return {
        approved_by: approval.approved_by ?? null,
        approved_at: approval.approved_at ?? null,
    };
}

export function readVersion(body) {
    if (!isObject(body)) throw new PlanContractError('no llegó un objeto');

    const { version } = body;
    if (!isObject(version)) throw new PlanContractError('falta `version`');
    if (!isPositiveInteger(version.number)) throw new PlanContractError('falta `version.number`');
    if (typeof version.state !== 'string' || !version.state) {
        throw new PlanContractError('falta `version.state`');
    }
    if (!isPositiveInteger(version.revision)) throw new PlanContractError('falta `version.revision`');

    if (!('content' in body)) throw new PlanContractError('falta `content`');
    if (body.content !== null && typeof body.content !== 'string') {
        throw new PlanContractError('`content` no es texto');
    }

    return {
        plan: isObject(body.plan) ? body.plan : null,

        id: version.id ?? null,
        number: version.number,
        state: version.state,
        revision: version.revision,
        author_id: version.author_id ?? null,
        document_version_id: version.document_version_id ?? null,
        created_at: version.created_at ?? null,
        updated_at: version.updated_at ?? null,

        content: body.content,
        content_version: body.content_version ?? null,
        scope: readScope(body.scope),
        approval: readApproval(body.approval),
    };
}

// ------------------------------------------------------------ the editor

// What the editor starts from. `content` null is shown as an empty box: that is
// what the version holds, and the save rules below keep an untouched empty box
// from being written back as a change.
export function editorValuesOf(model) {
    return {
        content: model.content ?? '',
        scope: model.scope.status,
        repository_ids: model.scope.repository_ids ?? [],
    };
}

const sameIds = (a, b) => a.length === b.length && a.every((id, index) => id === b[index]);
const sortedIds = ids => [...new Set((ids ?? []).map(Number))].sort((a, b) => a - b);

// Only what REALLY changed travels. Returns `{ payload }` or `{ error }`.
//
// `revision` is the one of `base` — the version as it was read — and never a
// fresher one: re-reading it to dodge a 409 is how the second writer flattens
// the first.
export function toSaveDraftPayload(values, base) {
    const payload = { revision: base.revision };

    const before = base.content ?? '';
    if (values.content !== before) {
        // The contract refuses empty content (min 1), and clearing a plan is not
        // an edit this screen should make by accident.
        if (!values.content.trim()) {
            return { error: 'El plan no puede quedar vacío. Deshacé el borrado o escribí el contenido.' };
        }
        payload.content = values.content;
    }

    // A scope that could not be read is never written: sending anything would
    // replace a scope nobody on this screen has seen.
    if (base.scope.status !== SCOPE_UNKNOWN && values.scope !== SCOPE_UNKNOWN) {
        const beforeIds = base.scope.repository_ids;
        const afterIds = values.scope === SCOPE_LISTED ? sortedIds(values.repository_ids) : [];

        // Specific repositories with none chosen would travel as `[]`, which
        // the contract reads as THE WHOLE PROJECT — the widest scope, reached by
        // leaving a box empty.
        if (values.scope === SCOPE_LISTED && afterIds.length === 0) {
            return { error: 'Elegí al menos un repositorio, o cambiá el alcance a todo el proyecto.' };
        }

        if (values.scope !== base.scope.status || !sameIds(beforeIds, afterIds)) {
            payload.repository_ids = afterIds;
        }
    }

    return { payload };
}

export function draftHasChanges(payload) {
    return Object.keys(payload).some(key => key !== 'revision');
}

export function isDirty(values, base) {
    if (values.content !== (base.content ?? '')) return true;
    if (base.scope.status === SCOPE_UNKNOWN) return false;
    if (values.scope !== base.scope.status) return true;
    return values.scope === SCOPE_LISTED
        && !sameIds(sortedIds(values.repository_ids), base.scope.repository_ids);
}

// Which version the editor should be based on when the server answers again.
//
// A newer read is adopted ONLY when nothing on screen would be lost: no unsaved
// changes and no conflict being resolved. Otherwise the old base stays, with
// its old revision, so the next save meets the 409 it deserves instead of
// silently writing over what somebody else saved.
export function nextBase(base, incoming, { dirty, conflict }) {
    if (!incoming || incoming.revision === base.revision) return base;
    if (dirty || conflict) return base;
    return incoming;
}

// ------------------------------------------------------------ reading

// What two versions can honestly be compared on. Text: both must HAVE text —
// `null` is not an empty document. Scope: both must have been read.
export function comparable(before, after) {
    const missingContent = [];
    if (typeof before?.content !== 'string') missingContent.push('before');
    if (typeof after?.content !== 'string') missingContent.push('after');

    return {
        content: missingContent.length === 0,
        missingContent,
        scope: !!before && !!after
            && before.scope.status !== SCOPE_UNKNOWN
            && after.scope.status !== SCOPE_UNKNOWN,
    };
}

// `repoName` resolves an id to its name: a scope is read by WHICH repositories
// it covers, not by how many. Without it, the count is the fallback.
export function scopeText(scope, repoName) {
    if (scope.status === SCOPE_WHOLE) return 'Todo el proyecto';
    if (scope.status === SCOPE_LISTED) {
        if (repoName) return scope.repository_ids.map(repoName).join(' · ');
        const count = scope.repository_ids.length;
        return `${count} repositorio${count === 1 ? '' : 's'}`;
    }
    return 'Alcance ilegible';
}
